/* External */
import {
  memo,
  useState,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

/* Ott-common */
import Loading from '@dtvgo/loading';

/* Utils */
import { useTitle } from '../../../../utils/metaDataUtils';
import { getPlayerLoaderSecondSubtitle, usePlayerLoaderSubtitle } from '../utils';

/* Styles */
import './LoaderScreen.scss';

const TRANSITION_TIME_MS = 300;

function LoaderScreen({
  isFullScreen = true,
  showMetadata = true,
  className = '',
  content = undefined,
  schedule = undefined,
  isContentRequestCompleted = false,
  hidden = false,
  channelName = '',
}) {
  const [isVisible, setIsVisible] = useState(true);
  const { timeZone } = useIntl();

  useEffect(() => {
    let timeout;
    if (hidden) {
      timeout = setTimeout(() => {
        setIsVisible(false);
      }, TRANSITION_TIME_MS);
    } else {
      setIsVisible(true);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [hidden]);

  const title = useTitle(content, schedule);
  const subtitle = usePlayerLoaderSubtitle(content, schedule);
  const subtitle2 = useMemo(
    () => getPlayerLoaderSecondSubtitle({
      content, schedule, timeZone, channelName,
    }),
    [content, schedule, timeZone, channelName],
  );

  const metadata = useMemo(() => {
    if (showMetadata) {
      return !title && isContentRequestCompleted ? (
        <span className="dtv-html5-c-loader-screen__missing-metadata">
          <FormattedMessage id="schedule.missing.metadata" />
        </span>
      ) : (
        <>
          <h1 className="dtv-html5-c-loader-screen__title">{title}</h1>
          <h2 className="dtv-html5-c-loader-screen__subtitle dtv-html5-c-loader-screen__subtitle--line2">
            {subtitle}
          </h2>
          <h3 className="dtv-html5-c-loader-screen__subtitle dtv-html5-c-loader-screen__subtitle--line3">
            {subtitle2}
          </h3>
        </>
      );
    }

    return undefined;
  }, [isContentRequestCompleted, showMetadata, subtitle, subtitle2, title]);

  return isVisible && (
    <div
      className={`dtv-html5-c-loader-screen ${
        hidden ? 'dtv-html5-c-loader-screen--hidden' : ''
      } ${className}
      ${!isFullScreen && 'dtv-html5-c-loader-screen--reduced-size'}
      ${!showMetadata && 'dtv-html5-c-loader-screen--no-metadata'}`}
    >
      <div className="dtv-html5-c-loader-screen__wrapper">
        <Loading />
        { metadata }
      </div>
    </div>
  );
}

LoaderScreen.propTypes = {
  isFullScreen: PropTypes.bool,
  showMetadata: PropTypes.bool,
  className: PropTypes.string,
  content: PropTypes.shape({}),
  schedule: PropTypes.shape({}),
  isContentRequestCompleted: PropTypes.bool,
  hidden: PropTypes.bool,
  channelName: PropTypes.string,
};

export default memo(LoaderScreen);
